import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { environment } from 'src/environments/environment';
import {
	IProcessCheckStatusResponse,
	IProcessConfigResponse,
	IProcessDesign,
	IProcessFinishResponse,
	IProcessStatusResponse,
	OPERATION_TYPE,
	PROCESS_STATUS,
	IDeviceRegisterNonceResponse,
	IDeviceRegisterResponse,
} from '../models/user-identification';
import { MasterService } from 'src/app/shared/services/master.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { IResultResponse } from 'src/app/shared/models/result';

@Injectable({
	providedIn: 'root',
})
export class ProcessService extends ApiService {
	// #region Services
	private masterService = inject(MasterService);
	private errorService = inject(ErrorService);
	// #endregion

	private unsubcribeCheckStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	/** NIF-650: /operation/start solo tras device/register (anti-prefetch). */
	private deviceRegistered$ = new BehaviorSubject<boolean>(false);
	private pendingInitOperations = new Set<OPERATION_TYPE>();
	/** NIF-944: un solo finish por overlay 3099 (idempotente en la sesión). */
	private systemUnavailableFinalizeStarted = false;

	constructor() {
		super();
	}

	public isDeviceRegistered(): boolean {
		return this.deviceRegistered$.value;
	}

	/** Marca el dispositivo como registrado y descarga initOperation pendientes. */
	public markDeviceRegistered(): void {
		this.deviceRegistered$.next(true);
		const pending = [...this.pendingInitOperations];
		this.pendingInitOperations.clear();
		for (const operation of pending) {
			this.postInitOperation(operation);
		}
	}

	/** Reset al reiniciar arranque (getInitialData / 1107). */
	public resetDeviceRegistrationGate(): void {
		this.deviceRegistered$.next(false);
		this.pendingInitOperations.clear();
	}

	public getConfig(): Observable<IProcessConfigResponse> {
		return this.http.get<IProcessConfigResponse>(
			environment.identity2 + `/process/config?device_id=${encodeURIComponent(this.auth.getDeviceId() ?? '')}`,
			{
				headers: this.getHeaders(),
			},
		);
	}

	public getDeviceRegisterNonce(): Observable<IDeviceRegisterNonceResponse> {
		return this.http.get<IDeviceRegisterNonceResponse>(
			environment.identity2 + `/process/device-register-nonce`,
			{ headers: this.getHeaders() },
		);
	}

	public registerDevice(registerNonce: string): Observable<IDeviceRegisterResponse> {
		return this.http.post<IDeviceRegisterResponse>(
			environment.identity2 + `/process/device/register`,
			{
				device_id: this.auth.getDeviceId(),
				register_nonce: registerNonce,
			},
			{ headers: this.getHeaders() },
		);
	}

	/**
	 * Diseño del proceso (spinner, tema, CSS) antes de cargar el resto de la config.
	 */
	public getConfigDesign(): Observable<IProcessDesign> {
		return this.http.get<IProcessDesign>(
			environment.identity2 + `/process/config_design?device_id=${this.auth.getDeviceId()}`,
			{
				headers: this.getHeaders(),
			},
		);
	}

	public getProcessStatus(): Observable<IProcessStatusResponse> {
		return this.http.get<IProcessStatusResponse>(
			environment.identity2 + `/process/status?device_id=${this.auth.getDeviceId()}`,
			{
				headers: this.getHeaders(),
			},
		);
	}

	public updateProcessStatus(status: PROCESS_STATUS): Observable<any> {
		const body = { state: status, device_id: this.auth.getDeviceId() };
		return this.http.patch<any>(environment.identity2 + `/process/status`, body, {
			headers: this.getHeaders(),
		});
	}

	/** NIF-1064: anota with_stream en el proceso tras WHIP OK (idempotente en API). */
	public markStreamingStarted(): Observable<{ with_stream: boolean }> {
		return this.http.post<{ with_stream: boolean }>(
			environment.identity2 + `/process/streaming/started`,
			{},
			{ headers: this.getHeaders() },
		);
	}

	public checkProcessStatus(): Observable<IProcessCheckStatusResponse> {
		return this.http.post<IProcessCheckStatusResponse>(
			environment.identity2 + `/process/check-status`,
			{},
			{ headers: this.getHeaders() },
		);
	}

	public getProcessInfo(process: string): Observable<IResultResponse> {
		return this.http.get<IResultResponse>(environment.identity2 + `/process/info?uuid=${process}`, {
			headers: this.getHeaders(),
		});
	}

	public cancelDevices(): Observable<Partial<IProcessStatusResponse>> {
		return this.http.get<Partial<IProcessStatusResponse>>(
			environment.identity2 + `/process/cancel_devices?device_id=${this.auth.getDeviceId()}`,
			{
				headers: this.getHeaders(),
			},
		);
	}

	public sendLocation(body: { lat: string; lon: string }): Observable<any> {
		return this.http.post<any>(environment.identity2 + `/location`, body, {
			headers: this.getHeaders(),
		});
	}

	public notifyInvalidProcess(): Observable<any> {
		return this.http.get<any>(environment.identity2 + `/process/unsolicited`, {
			headers: this.getHeaders(),
		});
	}

	public initOperation(operation: OPERATION_TYPE): void {
		if (!this.deviceRegistered$.value) {
			this.pendingInitOperations.add(operation);
			return;
		}
		this.postInitOperation(operation);
	}

	private postInitOperation(operation: OPERATION_TYPE): void {
		const operationTypeUuid = this.masterService.getUuidOperationType(operation);
		if (!operationTypeUuid) {
			return;
		}

		const body = {
			type: operationTypeUuid,
			operation_type: operation,
		};
		this.http
			.post<any>(environment.identity2 + `/operation/start`, body, { headers: this.getHeaders() })
			.subscribe({
				next: () => {},
				error: (error: HttpErrorResponse) => {
					this.errorService.hasGeneralError({ ...error?.error });
				},
			});
	}

	public finishOperation(): Observable<IProcessFinishResponse> {
		const body = { device_id: this.auth.getDeviceId() };
		return this.http.post<IProcessFinishResponse>(environment.identity2 + `/operation/finish`, body, {
			headers: this.getHeaders(),
		});
	}

	/**
	 * NIF-944: cierra el proceso en KO tras overlay 3099.
	 * El overlay no navega a Finish; sin esto el proceso quedaría IN_PROGRESS (NIF-970).
	 */
	public finalizeOnSystemUnavailable(): void {
		if (this.systemUnavailableFinalizeStarted) {
			return;
		}
		this.systemUnavailableFinalizeStarted = true;
		this.unSubcribeCheckStatus();
		this.finishOperation().subscribe({
			next: () => undefined,
			// Overlay 3099 ya visible; no pisar con 1005 u otro error de finish.
			error: () => undefined,
		});
	}

	get getUnsubcribeCheckStatus(): Observable<boolean> {
		return this.unsubcribeCheckStatus$.asObservable();
	}

	public unSubcribeCheckStatus() {
		this.unsubcribeCheckStatus$.next(true);
	}
}
